import React from 'react';
import { Timer, TrendingUp } from 'lucide-react';
import { useDemoReady, useDemoWarmup } from '../../hooks/useDemoWarmup';

const SLOW_MS = 12000;
const VERY_SLOW_MS = 25000;
const secs = (ms) => `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;

// Tools launcher → under Prepare demo: per-page load timing from the last warm-up, with
// slow pages highlighted and a marker when a page got noticeably slower than the run before.
export default function WarmupReport() {
  const ready = useDemoReady();
  const { running } = useDemoWarmup();
  if (running || !ready?.pages?.length) return null;
  const prevFor = (path) => ready.prevPages?.find((p) => p.path === path);
  return (
    <div className="mx-4 mb-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="warmup-report">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
        <Timer className="h-3.5 w-3.5 text-teal-600" /> Last warm-up · page load times
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]" data-testid="warmup-report-list">
        {ready.pages.map((p) => {
          const prev = prevFor(p.path);
          const slower = prev?.ok && p.ok && p.ms > prev.ms * 1.5 && p.ms - prev.ms > 3000;
          const tone = !p.ok ? 'text-rose-700' : p.ms >= VERY_SLOW_MS ? 'text-rose-700' : p.ms >= SLOW_MS ? 'text-amber-700' : 'text-slate-600';
          return (
            <li key={p.path} className={`flex items-center justify-between gap-2 ${tone}`} data-testid={`warmup-report-${p.path === '/' ? 'home' : p.path.replace(/\//g, '-').slice(1)}`} data-slow={p.ms >= SLOW_MS || !p.ok ? 'true' : 'false'}>
              <span className="truncate">{p.label}</span>
              <span className="flex items-center gap-1 font-mono tabular-nums">
                {slower && <TrendingUp className="h-3 w-3" aria-label="slower than last run" />}
                {p.ok ? secs(p.ms) : 'timed out'}
              </span>
            </li>
          );
        })}
      </ul>
      {ready.pages.some((p) => !p.ok || p.ms >= SLOW_MS) && (
        <p className="mt-1 text-[10.5px] text-amber-700" data-testid="warmup-report-hint">Amber/red pages took over {SLOW_MS / 1000}s to settle — open them once before the call.</p>
      )}
    </div>
  );
}
